import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, BookMarked, ExternalLink, Search, X } from "lucide-react";
import {
  api,
  type WiedzaItem,
  type WiedzaTrack,
} from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { cn } from "@/lib/utils";

function fold(text: string) {
  return (text || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ł/g, "l");
}

function matchesQuery(item: WiedzaItem, q: string) {
  if (!q.trim()) return true;
  const hay = fold([item.title, item.summary, item.path, item.category, ...(item.tags || [])].join(" "));
  return fold(q)
    .split(/\s+/)
    .filter(Boolean)
    .every((token) => hay.includes(token));
}

function formatLabel(fmt: string) {
  if (fmt === "md") return "markdown";
  if (fmt === "html") return "html";
  if (fmt === "pdf") return "pdf";
  return fmt;
}

function ItemCard({
  item,
  active,
  categoryLabel,
  onOpen,
}: {
  item: WiedzaItem;
  active: boolean;
  categoryLabel: string;
  onOpen: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        "w-full rounded-lg border p-3 text-left transition hover:border-brand/40 hover:bg-muted/40",
        active && "border-brand bg-brand/5",
        !item.available && "opacity-70",
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="font-medium leading-snug">{item.title}</p>
          <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{item.summary}</p>
        </div>
        {item.featured ? <Badge variant="secondary">start</Badge> : null}
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        <Badge variant="outline">{categoryLabel}</Badge>
        <Badge variant="outline">{formatLabel(item.format)}</Badge>
        {item.minutes ? <Badge variant="outline">~{item.minutes} min</Badge> : null}
        {!item.available ? <Badge variant="destructive">brak pliku</Badge> : null}
        {(item.tags || []).slice(0, 3).map((tag) => (
          <Badge key={tag} variant="secondary">
            {tag}
          </Badge>
        ))}
      </div>
    </button>
  );
}

export default function Wiedza() {
  const [params, setParams] = useSearchParams();
  const selectedId = params.get("id") || "";
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [tag, setTag] = useState("");
  const [trackId, setTrackId] = useState("");
  const [mobileShowDetail, setMobileShowDetail] = useState(Boolean(selectedId));

  const catalogQuery = useQuery({
    queryKey: ["wiedza-catalog"],
    queryFn: () => api.wiedzaCatalog(),
  });

  const detailQuery = useQuery({
    queryKey: ["wiedza-item", selectedId],
    queryFn: () => api.wiedzaItem(selectedId),
    enabled: Boolean(selectedId),
  });

  const catalog = catalogQuery.data;
  const items = catalog?.items || [];
  const categories = catalog?.categories || [];
  const tracks = catalog?.tracks || [];

  const categoryLabel = useMemo(() => {
    const map = new Map(categories.map((c) => [c.id, c.label]));
    return (id: string) => map.get(id) || id;
  }, [categories]);

  const allTags = useMemo(() => {
    const set = new Set<string>();
    items.forEach((item) => (item.tags || []).forEach((t) => set.add(t)));
    return Array.from(set).sort((a, b) => a.localeCompare(b, "pl"));
  }, [items]);

  const activeTrack: WiedzaTrack | undefined = tracks.find((t) => t.id === trackId);

  const filtered = useMemo(() => {
    return items.filter((item) => {
      if (category && item.category !== category) return false;
      if (tag && !(item.tags || []).includes(tag)) return false;
      if (activeTrack?.tags?.length) {
        const hit = activeTrack.tags.some((t) => (item.tags || []).includes(t));
        if (!hit) return false;
      }
      if (!matchesQuery(item, q)) return false;
      return true;
    });
  }, [items, category, tag, activeTrack, q]);

  const topLevel = useMemo(
    () => filtered.filter((item) => !item.parentId || !filtered.some((p) => p.id === item.parentId)),
    [filtered],
  );

  const childrenOf = (parentId: string) => filtered.filter((item) => item.parentId === parentId);

  const selected = items.find((item) => item.id === selectedId) || detailQuery.data?.item;
  const childrenSelected = selectedId ? items.filter((item) => item.parentId === selectedId) : [];
  const parentOfSelected = selected?.parentId
    ? items.find((item) => item.id === selected.parentId)
    : undefined;

  useEffect(() => {
    if (selectedId) setMobileShowDetail(true);
  }, [selectedId]);

  function openItem(id: string) {
    const next = new URLSearchParams(params);
    next.set("id", id);
    setParams(next, { replace: false });
    setMobileShowDetail(true);
  }

  function clearSelection() {
    const next = new URLSearchParams(params);
    next.delete("id");
    setParams(next, { replace: true });
    setMobileShowDetail(false);
  }

  function applyTrack(track: WiedzaTrack | null) {
    setTrackId(track?.id || "");
    setTag("");
  }

  const rawUrl = selectedId ? api.wiedzaRawUrl(selectedId) : "";

  return (
    <div className="space-y-4">
      <PageHeader
        title="Wiedza"
        subtitle="Kursy, playbooki i ściągi w jednym miejscu. Szukaj albo wejdź ścieżką."
      />

      <div className="grid gap-2 sm:grid-cols-3">
        {tracks.map((track) => (
          <button
            key={track.id}
            type="button"
            onClick={() => applyTrack(trackId === track.id ? null : track)}
            className={cn(
              "rounded-lg border p-3 text-left transition hover:border-brand/40",
              trackId === track.id && "border-brand bg-brand/5",
            )}
          >
            <p className="flex items-center gap-2 text-sm font-semibold">
              <BookMarked className="h-4 w-4 text-brand" />
              {track.label}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">{track.summary}</p>
          </button>
        ))}
      </div>

      <Card>
        <CardContent className="flex flex-col gap-3 pt-4 sm:flex-row sm:flex-wrap sm:items-end">
          <label className="min-w-[12rem] flex-1 space-y-1 text-sm">
            <span className="text-muted-foreground">Szukaj</span>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-8"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="np. hook, MOFU, Diament…"
              />
            </div>
          </label>
          <label className="space-y-1 text-sm sm:w-48">
            <span className="text-muted-foreground">Kategoria</span>
            <Select
              aria-label="Kategoria"
              value={category}
              onChange={setCategory}
              options={[
                { value: "", label: "wszystkie" },
                ...categories.map((c) => ({ value: c.id, label: c.label })),
              ]}
            />
          </label>
          <label className="space-y-1 text-sm sm:w-44">
            <span className="text-muted-foreground">Tag</span>
            <Select
              aria-label="Tag"
              value={tag}
              onChange={setTag}
              options={[
                { value: "", label: "wszystkie" },
                ...allTags.map((t) => ({ value: t, label: t })),
              ]}
            />
          </label>
          {(q || category || tag || trackId) && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setQ("");
                setCategory("");
                setTag("");
                setTrackId("");
              }}
            >
              <X className="mr-1 h-4 w-4" />
              Wyczyść
            </Button>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]">
        <Card
          className={cn(
            "lg:block",
            mobileShowDetail && selectedId ? "hidden lg:block" : "block",
          )}
        >
          <CardHeader className="pb-2">
            <CardTitle className="text-base">
              Lista ({topLevel.length}
              {filtered.length !== items.length ? ` z ${items.length}` : ""})
            </CardTitle>
            {catalogQuery.isLoading ? (
              <p className="text-sm text-muted-foreground">Ładuję katalog…</p>
            ) : catalogQuery.isError ? (
              <p className="text-sm text-destructive">Nie udało się wczytać katalogu.</p>
            ) : (
              <p className="text-xs text-muted-foreground">
                Dostępne pliki: {catalog?.available_count ?? 0}/{catalog?.count ?? 0}
              </p>
            )}
          </CardHeader>
          <CardContent className="max-h-[70vh] space-y-2 overflow-y-auto">
            {topLevel.map((item) => (
              <div key={item.id} className="space-y-1.5">
                <ItemCard
                  item={item}
                  active={selectedId === item.id}
                  categoryLabel={categoryLabel(item.category)}
                  onOpen={() => openItem(item.id)}
                />
                {childrenOf(item.id).map((child) => (
                  <div key={child.id} className="pl-3">
                    <ItemCard
                      item={child}
                      active={selectedId === child.id}
                      categoryLabel={categoryLabel(child.category)}
                      onOpen={() => openItem(child.id)}
                    />
                  </div>
                ))}
              </div>
            ))}
            {!catalogQuery.isLoading && topLevel.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nic nie pasuje do filtrów.</p>
            ) : null}
          </CardContent>
        </Card>

        <Card
          className={cn(
            "min-h-[24rem]",
            mobileShowDetail && selectedId ? "block" : "hidden lg:block",
          )}
        >
          {!selectedId ? (
            <CardContent className="flex h-full min-h-[24rem] items-center justify-center p-6 text-center text-sm text-muted-foreground">
              Wybierz pozycję z listy albo ścieżkę u góry.
            </CardContent>
          ) : (
            <>
              <CardHeader className="space-y-3 border-b pb-4">
                <div className="flex flex-wrap items-center gap-2 lg:hidden">
                  <Button type="button" variant="ghost" size="sm" onClick={clearSelection}>
                    <ArrowLeft className="mr-1 h-4 w-4" />
                    Lista
                  </Button>
                </div>
                {parentOfSelected ? (
                  <button
                    type="button"
                    className="text-left text-xs text-brand underline"
                    onClick={() => openItem(parentOfSelected.id)}
                  >
                    ← {parentOfSelected.title}
                  </button>
                ) : null}
                <div>
                  <CardTitle className="text-xl leading-snug">
                    {selected?.title || selectedId}
                  </CardTitle>
                  <p className="mt-1 text-sm text-muted-foreground">{selected?.summary}</p>
                  <p className="mt-2 font-mono text-xs text-muted-foreground">{selected?.path}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {rawUrl ? (
                    <Button asChild variant="outline" size="sm">
                      <a href={rawUrl} target="_blank" rel="noreferrer">
                        <ExternalLink className="mr-1 h-4 w-4" />
                        Otwórz raw
                      </a>
                    </Button>
                  ) : null}
                  <Button asChild variant="ghost" size="sm">
                    <Link to="/plan-tygodnia">Plan tygodnia</Link>
                  </Button>
                  <Button asChild variant="ghost" size="sm">
                    <Link to="/karuzele">Karuzele</Link>
                  </Button>
                </div>
                {childrenSelected.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {childrenSelected.map((child) => (
                      <Button
                        key={child.id}
                        type="button"
                        size="sm"
                        variant={selectedId === child.id ? "default" : "outline"}
                        onClick={() => openItem(child.id)}
                      >
                        {child.title.replace(/^Lekcja\s+/i, "").replace(/^Deck\s+/i, "")}
                      </Button>
                    ))}
                  </div>
                ) : null}
              </CardHeader>
              <CardContent className="p-0">
                {detailQuery.isLoading ? (
                  <p className="p-6 text-sm text-muted-foreground">Ładuję treść…</p>
                ) : detailQuery.isError ? (
                  <p className="p-6 text-sm text-destructive">
                    Nie udało się wczytać pliku. Może jeszcze nie ma go w paczce na serwerze.
                  </p>
                ) : selected?.format === "html" || selected?.format === "pdf" ? (
                  <iframe
                    title={selected?.title || "Dokument"}
                    src={rawUrl}
                    className="h-[70vh] w-full border-0 bg-white"
                  />
                ) : (
                  <div className="wiedza-prose max-h-[70vh] overflow-y-auto px-4 py-5 sm:px-6">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {detailQuery.data?.body || "_Brak treści._"}
                    </ReactMarkdown>
                  </div>
                )}
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
